/* Plantica — Cart drawer

   The cart itself is the theme's: <cart-drawer>, <cart-items> and
   <quantity-input> do the API calls, the debouncing and the re-render. This
   file only fills the gap they leave.

   The count in the drawer head is not here: it is the same element as the
   header's cart bubble, in plantica-cart-count.js, because both leave the
   order cover out of the number. */

/* ---------------------------------------------------------------------------
   An empty cart, pointed somewhere

   The cards offered under „Coșul tău este gol" are the header's own banners —
   the conditions someone shops by. They are copied out of the menu drawer,
   which the header renders on every page, rather than configured a second time
   in the cart: one list to edit, one place it can be wrong.

   The empty state lives inside the region the theme swaps on every cart
   update, so this element is created anew each time and fills itself on
   connect. With no banners, or no JavaScript, it stays hidden and the empty
   cart is what it was.
   --------------------------------------------------------------------------- */

(() => {
  if (customElements.get('plantica-cart-guide')) return;

  class PlanticaCartGuide extends HTMLElement {
    connectedCallback() {
      const source = document.querySelector('#MenuDrawer .plantica-drawer__banners');
      const slot = this.querySelector('[data-cart-banners]');
      if (!source || !slot || slot.children.length) return;

      const copy = source.cloneNode(true);
      // Ids and the customizer's own hooks belong to the original; a second
      // element carrying them would make the editor highlight the wrong card.
      copy.removeAttribute('id');
      copy.querySelectorAll('[id], [data-shopify-editor-block]').forEach((node) => {
        node.removeAttribute('id');
        node.removeAttribute('data-shopify-editor-block');
      });

      slot.appendChild(copy);
      this.hidden = false;
    }
  }

  customElements.define('plantica-cart-guide', PlanticaCartGuide);
})();

/* ---------------------------------------------------------------------------
   Cart keep-timer

   Counts down how long the cart is kept. The deadline is stored, not the
   remaining seconds, so a refresh continues the same countdown instead of
   restarting it — that was the whole point of persisting it.

   Rules, as agreed:
     starts   when the cart first goes from empty to not empty
     survives refreshes and new items — adding does not buy more time
     ends     showing "Coșul tău te așteaptă", never restarting on its own
     clears   when the cart is emptied

   localStorage rather than a cookie: a cookie would be sent with every request
   for no reason, and Liquid cannot read it anyway.
   --------------------------------------------------------------------------- */

(() => {
  if (customElements.get('plantica-cart-timer')) return;

  const KEY = 'plantica:cart-deadline';

  const read = () => {
    try {
      const value = parseInt(window.localStorage.getItem(KEY), 10);
      return Number.isFinite(value) ? value : null;
    } catch (e) {
      return null; // private mode, or storage disabled
    }
  };

  const write = (value) => {
    try {
      if (value === null) window.localStorage.removeItem(KEY);
      else window.localStorage.setItem(KEY, String(value));
    } catch (e) {
      /* nothing to do: the timer just restarts next visit */
    }
  };

  class PlanticaCartTimer extends HTMLElement {
    connectedCallback() {
      this.titleEl = this.querySelector('[data-timer-title]');
      this.timeEl = this.querySelector('[data-timer-time]');
      this.defaultTitle = this.titleEl ? this.titleEl.textContent.trim() : '';

      this.start(parseInt(this.dataset.items, 10) || 0);

      const theme = window.FoxTheme;
      if (theme && theme.pubsub) {
        this.unsubscribe = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || !event.cart || event.cart.errors) return;
          this.start(event.cart.item_count);
        });
      }
    }

    disconnectedCallback() {
      this.stop();
      if (this.unsubscribe) this.unsubscribe();
    }

    stop() {
      if (this.interval) window.clearInterval(this.interval);
      this.interval = null;
    }

    start(itemCount) {
      this.stop();

      if (itemCount === 0) {
        write(null);
        this.hidden = true;
        return;
      }

      this.hidden = false;

      let deadline = read();
      if (deadline === null || deadline <= Date.now()) {
        // No deadline yet means this is the first item. An expired one is left
        // expired: adding another product does not buy more time.
        if (deadline === null) {
          const minutes = parseInt(this.dataset.minutes, 10) || 30;
          deadline = Date.now() + minutes * 60 * 1000;
          write(deadline);
        }
      }

      this.deadline = deadline;
      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    tick() {
      const remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        this.stop();
        this.classList.add('is-expired');
        if (this.titleEl) this.titleEl.textContent = this.dataset.expiredText || this.defaultTitle;
        if (this.timeEl) this.timeEl.hidden = true;
        return;
      }

      const total = Math.floor(remaining / 1000);
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;

      if (this.timeEl) {
        this.timeEl.hidden = false;
        this.timeEl.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
      }
    }
  }

  customElements.define('plantica-cart-timer', PlanticaCartTimer);
})();

/* ---------------------------------------------------------------------------
   Accessory carousel

   Swiper comes from the theme's vendor bundle, and the arrows are the theme's
   own buttons. Adding follows the theme's own path: POST to the cart, then
   dispatch `cart:refresh`, which is what <cart-drawer> listens for — so the
   drawer re-renders itself, totals and free-shipping bar included.
   --------------------------------------------------------------------------- */

(() => {
  if (customElements.get('plantica-cart-upsell')) return;

  class PlanticaCartUpsell extends HTMLElement {
    connectedCallback() {
      this.initSlider();
      this.addEventListener('click', this.onClick.bind(this));
    }

    disconnectedCallback() {
      if (this.swiper) this.swiper.destroy(true, true);
    }

    initSlider() {
      const api = window.FoxTheme && window.FoxTheme.Swiper;
      const viewport = this.querySelector('.swiper');
      if (!api || !api.Swiper || !viewport) return; // still scrolls; see the CSS

      const { Swiper, Navigation } = api;
      this.swiper = new Swiper(viewport, {
        modules: [Navigation],
        slidesPerView: 'auto',
        spaceBetween: 10,
        watchOverflow: true,
        navigation: {
          prevEl: this.querySelector('.swiper-button-prev'),
          nextEl: this.querySelector('.swiper-button-next'),
        },
      });
    }

    async onClick(event) {
      const button = event.target.closest('.plantica-cart__upsell-add');
      if (!button || button.disabled) return;

      const id = button.dataset.variantId;
      if (!id) return;

      const label = button.textContent;
      button.disabled = true;
      if (button.dataset.addingLabel) button.textContent = button.dataset.addingLabel;

      try {
        const routes = (window.FoxTheme && window.FoxTheme.routes) || {};
        const response = await fetch(routes.cart_add_url || '/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
        });

        if (!response.ok) throw new Error('add failed');

        // From the drawer, the drawer stays open; on /cart, the page redraws.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: !this.closest('main-cart') } }));
      } catch (error) {
        // The drawer is open and the item is not in it: say so rather than
        // leaving a button that looks like it worked.
        button.disabled = false;
        button.textContent = label;
      }
    }
  }

  customElements.define('plantica-cart-upsell', PlanticaCartUpsell);
})();

/* ---------------------------------------------------------------------------
   Closing the drawer from inside a re-rendered region

   The theme binds its `aria-controls` listeners once, when the drawer connects.
   Everything inside #CartDrawerBody / Footer / Empty is replaced on the first
   cart change, so a button there stops closing anything. Delegation survives
   the swap. Once per page: on /cart this file is loaded by the drawer and by
   the page.
   --------------------------------------------------------------------------- */

(() => {
  if (window.planticaCartClose) return;
  window.planticaCartClose = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-close]');
    if (!trigger) return;

    const drawer = document.getElementById('CartDrawer');
    if (!drawer || typeof drawer.hide !== 'function') return;

    event.preventDefault();
    drawer.hide();
  });
})();

/* ---------------------------------------------------------------------------
   Order cover

   A real product, sold as a line the customer decides about. The tick reflects
   whether that line is in the cart — the cart is the truth, the checkbox only
   reports it. That is why the markup is rendered by Liquid from `cart.items`
   and this only ever changes the cart and asks for a re-render.

   Pre-ticked means: added on the customer's behalf once the cart holds
   something else. Two rules keep that from becoming a nuisance —

     • unticking is remembered, so it never comes back on its own
     • a cart holding nothing but the cover has it taken back out

   ⚠️ A pre-ticked box that adds a charge needs express consent under OUG
   34/2014 art. 22 (EU 2011/83 art. 22). The default is a setting for that
   reason: turn it off and the line becomes an offer rather than an opt-out.
   ------------------------------------------------------------------------- */

(() => {
  if (customElements.get('plantica-cart-insurance')) return;

  const KEY = 'plantica:cover-optout';

  // Private windows and blocked storage throw on access, so every read and
  // write is guarded. Losing the preference is survivable; a thrown error in
  // the cart is not.
  const optedOut = () => {
    try {
      return localStorage.getItem(KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const remember = (out) => {
    try {
      if (out) localStorage.setItem(KEY, '1');
      else localStorage.removeItem(KEY);
    } catch (error) {
      /* nothing to do */
    }
  };

  const route = (name, fallback) => {
    const routes = (window.FoxTheme && window.FoxTheme.routes) || {};
    return routes[name] || fallback;
  };

  /* One queue for every instance of this element, rather than one flag inside
     each of them.

     The element is re-rendered together with the drawer, so each cart update
     produces a fresh instance carrying a fresh flag. Three of them read the
     cart, all three saw no cover, and all three added one: three sets went in
     and the cover came out at x3 — 9 lei instead of 3.

     It never showed while our own offer box did the adding, because that sent
     everything in one request and so raised one cart update. Rapi Bundle adds
     in several steps, and the flaw had been waiting for exactly that.

     Serialising here means the second reconcile reads the cart the first one
     left behind, instead of the one it started from. */
  let queue = Promise.resolve();
  const serial = (task) => {
    const next = queue.then(task, task);
    queue = next.catch(() => {});
    return next;
  };

  class PlanticaCartInsurance extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.plantica-cart__insurance-input');
      this.variantId = Number(this.dataset.variantId);
      if (!this.input || !this.variantId) return;

      this.input.addEventListener('change', this.onChange);
      this.reconcile();
    }

    onChange = () => {
      const wanted = this.input.checked;
      remember(!wanted);
      return serial(() => this.write(wanted));
    };

    reconcile() {
      return serial(async () => {
        const cart = await this.read();
        if (!cart) return;

        const items = cart.items || [];
        const line = items.find((item) => item.variant_id === this.variantId);
        const others = items.some((item) => item.variant_id !== this.variantId);

        // Cover alone in the cart is not a cart.
        if (line && !others) return this.write(false);

        // One cover per order, never per item. A cart that already carries two
        // or three is put back to one here rather than left for the customer to
        // find at checkout.
        if (line && line.quantity > 1) return this.setQuantity(1);

        if (line || !others) return;
        if (this.dataset.defaultOn !== 'true' || optedOut()) return;

        await this.write(true);
      });
    }

    async read() {
      try {
        const response = await fetch(route('cart_url', '/cart') + '.js', {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    write(on) {
      return this.post(
        on ? route('cart_add_url', '/cart/add') : route('cart_change_url', '/cart/change'),
        on
          ? { items: [{ id: this.variantId, quantity: 1 }] }
          : { id: String(this.variantId), quantity: 0 },
        on
      );
    }

    setQuantity(quantity) {
      return this.post(
        route('cart_change_url', '/cart/change'),
        { id: String(this.variantId), quantity: quantity },
        true
      );
    }

    async post(url, body, on) {
      this.classList.add('is-busy');

      try {
        const response = await fetch(url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error('cover ' + response.status);

        // The drawer is already open; refresh it without re-opening.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
      } catch (error) {
        // Put the tick back where the cart actually is, rather than leaving it
        // claiming something that did not happen.
        this.input.checked = !on;
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }

  customElements.define('plantica-cart-insurance', PlanticaCartInsurance);
})();

/* ---------------------------------------------------------------------------
   Free shipping: the bar on its way, and the moment it gets there

   The band is re-rendered with the rest of the drawer on every cart change, so
   the theme's bar inside it is a new element each time and jumped straight to
   its new width. The last width is kept — in sessionStorage, so it carries
   over to the next page — and the new bar is put back there for a frame and
   then let go: it grows, or shrinks, from where it was, and its colour, which
   follows the width (plantica-cart.css), turns with it.

   Crossing the line throws confetti, once (05.10.2026, the client's request):
   when the cart goes from under the threshold to over it. Not when a drawer is
   opened on a cart that was already over, and not on a first visit, when there
   is nothing to compare with. The burst waits for the drawer to be open and
   the bar to fill, so it lands on a full green bar.

   /cart has a band of its own, in the page (26.10.2026). It is compared only
   with its own last drawing in the same visit, so a change made on the page
   grows its bar and throws its confetti there; the drawer's band, closed
   behind the page, leaves the celebrating to it.

   Nothing moves for someone who asked their system for less motion.
   --------------------------------------------------------------------------- */

(() => {
  if (customElements.get('plantica-cart-shipping')) return;

  const KEY = 'plantica-cart-shipping';
  const FILL_MS = 700; // the bar's transition, delay included (plantica-cart.css)
  const COLOURS = ['#b8e547', '#121212', '#d63a2f', '#ffc83d', '#7cc4ff'];

  const calm = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const read = () => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY));
    } catch (error) {
      return null;
    }
  };

  const write = (state) => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(state));
    } catch (error) {
      /* private mode: the bar simply jumps, as it used to */
    }
  };

  // The page's band: its last drawing in this visit only.
  let pageLast = null;

  // The theme sets `active` on the drawer once it has started to slide in. The
  // page is always open.
  const whenOpen = (drawer) =>
    new Promise((resolve) => {
      if (!drawer || drawer.localName !== 'cart-drawer' || drawer.hasAttribute('active')) {
        resolve();
        return;
      }
      const observer = new MutationObserver(() => {
        if (!drawer.hasAttribute('active')) return;
        observer.disconnect();
        resolve();
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['active'] });
    });

  // Thrown up from along the bar, falling across the panel. Each piece goes
  // up decelerating and comes down accelerating — two keyframe segments, each
  // with its own easing — spinning and fading on the way down.
  const burst = (band) => {
    const host = band.closest('.drawer__inner, .plantica-cart-page__main');
    const bar = band.querySelector('progress-bar');
    if (!host || !bar) return;

    const frame = host.getBoundingClientRect();
    const from = bar.getBoundingClientRect();
    const layer = document.createElement('div');
    layer.className = 'plantica-cart__confetti';
    layer.setAttribute('aria-hidden', 'true');
    host.appendChild(layer);

    const flights = [];
    for (let i = 0; i < 90; i += 1) {
      const piece = document.createElement('span');
      const size = 5 + Math.random() * 5;
      piece.style.left = `${from.left - frame.left + Math.random() * from.width}px`;
      piece.style.top = `${from.top - frame.top}px`;
      piece.style.width = `${size}px`;
      piece.style.height = `${Math.random() < 0.5 ? size * 0.45 : size}px`;
      piece.style.background = COLOURS[i % COLOURS.length];
      piece.style.borderRadius = Math.random() < 0.3 ? '50%' : '1px';
      layer.appendChild(piece);

      const angle = ((-90 + (Math.random() - 0.5) * 120) * Math.PI) / 180;
      const speed = 140 + Math.random() * 220;
      const drift = Math.cos(angle) * speed;
      const rise = Math.sin(angle) * speed;
      // In the drawer, across the panel; on the page, whose column can be long,
      // about a screen's worth.
      const depth = host.classList.contains('drawer__inner') ? frame.height : Math.min(frame.height, 700);
      const fall = depth * (0.5 + Math.random() * 0.45);
      const spin = (Math.random() - 0.5) * 1440;

      flights.push(
        piece.animate(
          [
            { transform: 'translate3d(0, 0, 0) rotate(0deg)', opacity: 1, easing: 'cubic-bezier(0.15, 0.7, 0.35, 1)' },
            {
              transform: `translate3d(${drift * 0.7}px, ${rise}px, 0) rotate(${spin * 0.35}deg)`,
              opacity: 1,
              offset: 0.3,
              easing: 'cubic-bezier(0.55, 0, 0.85, 0.45)',
            },
            { transform: `translate3d(${drift}px, ${fall}px, 0) rotate(${spin}deg)`, opacity: 0 },
          ],
          { duration: 1600 + Math.random() * 900, fill: 'forwards' }
        ).finished
      );
    }

    Promise.allSettled(flights).then(() => layer.remove());
  };

  // One celebration at a time in each place — the drawer, the page — and not
  // tied to the band that noticed the line being crossed: adding from a product
  // page can redraw the drawer twice, and the first band is gone by the time
  // the drawer is open. The burst goes to whichever band is there then — if the
  // cart is still over the line.
  const pending = new WeakSet();

  const celebrate = (place) => {
    if (!place || pending.has(place) || calm()) return;
    pending.add(place);

    whenOpen(place)
      .then(() => new Promise((resolve) => setTimeout(resolve, FILL_MS)))
      .then(() => {
        pending.delete(place);
        const band = place.querySelector('plantica-cart-shipping');
        const goal = band && band.querySelector('free-shipping-goal');
        if (!goal || !goal.classList.contains('free-shipping-goal--done')) return;
        band.classList.add('is-celebrating');
        burst(band);
      });
  };

  class PlanticaCartShipping extends HTMLElement {
    connectedCallback() {
      // The theme's <free-shipping-goal> inside upgrades after this element;
      // its numbers are there once the current round of reactions is over.
      queueMicrotask(() => this.settle());
    }

    settle() {
      const goal = this.querySelector('free-shipping-goal');
      const bar = this.querySelector('progress-bar');
      if (!goal || !bar || typeof goal.percent !== 'number') return;

      const percent = Math.round(goal.percent * 10) / 10;
      const done = goal.classList.contains('free-shipping-goal--done');
      const page = this.closest('main-cart');
      const last = page ? pageLast : read();
      if (page) pageLast = { percent, done };
      else write({ percent, done });

      if (last && last.percent !== percent && !calm()) this.grow(bar, last.percent);
      if (!done || !last || last.done) return;
      if (page) celebrate(page);
      else if (!document.querySelector('main-cart plantica-cart-shipping')) celebrate(this.closest('cart-drawer'));
    }

    // Back to the old width for a frame, then to the new one: the bar's own
    // transition does the rest.
    grow(bar, previous) {
      const target = bar.style.getPropertyValue('--percent');
      if (!target) return;

      bar.style.setProperty('--percent', `${previous}%`);
      getComputedStyle(bar, '::before').width; // fix the old width before moving on
      requestAnimationFrame(() => {
        requestAnimationFrame(() => bar.style.setProperty('--percent', target));
      });
    }
  }

  customElements.define('plantica-cart-shipping', PlanticaCartShipping);
})();

/* ---------------------------------------------------------------------------
   /cart: a refresh that renders the page's list too

   On /cart the theme's list (<main-cart>) re-renders from every cart update,
   and reads one that does not carry its own section as an emptied cart: it
   marks the page `is-empty` and removes the list. The theme's `cart:refresh`
   renders the drawer alone. The theme keeps its drawer off /cart, so that
   never met the list; ours stays, and the order cover it adds as the page
   loads refreshed it. With products in the cart, /cart said „Coșul tău este
   momentan gol." and had no checkout button (found 26.10.2026 on the live
   site).

   So on a page with the list, `cart:refresh` — ours, the theme's, an app's —
   is answered here, before the drawer hears it: every section that asks for
   cart updates (`cart:grouped-sections`, the theme's own roll call, which the
   drawer and the list both answer) is rendered in one request, as the theme
   does after a quantity change.
   --------------------------------------------------------------------------- */

(() => {
  if (window.planticaCartPageRefresh) return;
  window.planticaCartPageRefresh = true;

  let latest = 0;

  const refresh = async (detail) => {
    const theme = window.FoxTheme;
    const asked = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: asked } })
    );
    const sections = [...new Set(asked.filter(Boolean))];
    const ticket = ++latest;

    try {
      const [cart, rendered] = await Promise.all([
        fetch(`${theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
        fetch(`${theme.routes.root_url}?sections=${sections.join(',')}`).then((r) => r.json()),
      ]);
      // A later refresh has the newer cart.
      if (ticket !== latest) return;

      const drawer = document.querySelector('cart-drawer');
      const inDrawer = (drawer && drawer.hasAttribute('active')) || (detail && detail.open === true);
      const before = document.activeElement;

      cart.sections = rendered;
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });

      if (inDrawer) {
        if (detail && detail.open === true && drawer && typeof drawer.show === 'function') drawer.show();
      } else {
        // The theme's list gives the focus to its first product after every
        // update and keeps Tab inside the list — right after its own quantity
        // changes, not after this: the cover going in as the page loads put a
        // focus ring on the first product. The focus goes back where it was.
        if (theme.a11y && typeof theme.a11y.removeTrapFocus === 'function') theme.a11y.removeTrapFocus();
        if (before && before !== document.body && before.isConnected) before.focus({ preventScroll: true });
        else if (document.activeElement && document.activeElement !== document.body) document.activeElement.blur();
      }
    } catch (error) {
      // The page keeps what it showed; the next change or a reload sets it right.
    }
  };

  // Capturing on window runs before the drawer's own listener on document.
  window.addEventListener(
    'cart:refresh',
    (event) => {
      const theme = window.FoxTheme;
      if (!document.querySelector('main-cart') || !theme || !theme.pubsub || !theme.routes) return;
      event.stopImmediatePropagation();
      refresh(event.detail);
    },
    true
  );
})();
